import { useMemo, useState } from "react";
import { Modal, Form, Select, InputNumber, Switch, message, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/searchBar";
import StatusTabs from "../../components/statusTabs";
import AddButton from "../../components/addButton";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import { useGetEmployeeDropdownQuery, useAddWalletMutation, useGetWalletsQuery } from "../../services/wallet";
import { WalletTransactionsRoute } from "../../routes/routepath";
import usePermissions from "../../hooks/usePermissions";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface WalletRecord {
  key:           string;
  employeeId?:   string;
  employeeName?: string;
  walletAmount?: number;
  isActive?:     boolean;
}

/* ─── Component ──────────────────────────────────────────────────────── */

const Wallet = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [page,        setPage]        = useState(1);
  const [limit,       setLimit]       = useState(10);
  const [searchText,  setSearchText]  = useState("");
  const [activeTab,   setActiveTab]   = useState("all");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [form] = Form.useForm();

  const { data: employeeDropdownData, isFetching: employeesLoading } = useGetEmployeeDropdownQuery(undefined, { skip: !addModalOpen });
  const [addWallet, { isLoading: addingWallet }] = useAddWalletMutation();

  const employeeOptions = ((employeeDropdownData as any)?.data || []).map((e: any) => ({
    value: e._id,
    label: e.name || 'Unknown',
  }));

  const { data: walletsResp, isFetching: walletsLoading } = useGetWalletsQuery({
    status: activeTab !== 'all' ? (activeTab as 'active' | 'inactive') : undefined,
    search: searchText || undefined,
  });

  const apiData = walletsResp as any;
  const counts  = apiData?.counts || {};

  const allWallets = useMemo<WalletRecord[]>(() => {
    const rows: any[] = apiData?.data || [];
    return rows.map((w: any) => ({
      key:          w._id,
      employeeId:   w.employee?._id,
      employeeName: w.employee?.user?.name || '-',
      walletAmount: w.walletAmount,
      isActive:     w.isActive,
    }));
  }, [apiData]);

  const walletData = allWallets.slice((page - 1) * limit, page * limit);

  const tabsData = [
    { key: 'all',      label: 'All',      count: counts.all      || 0 },
    { key: 'active',   label: 'Active',   count: counts.active   || 0 },
    { key: 'inactive', label: 'Inactive', count: counts.inactive || 0 },
  ];

  const columns = [
    { title: 'S.No',     key: 'sno', width: 60, render: (_: unknown, __: unknown, i: number) => (page - 1) * limit + i + 1 },
    { title: 'Employee', dataIndex: 'employeeName', key: 'employeeName',
      render: (name: string, record: WalletRecord) => (
        <span
          style={{ color: '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => navigate(`${WalletTransactionsRoute}?employeeId=${record.employeeId}`)}
        >
          {name}
        </span>
      ) },
    { title: 'Wallet Amount', dataIndex: 'walletAmount', key: 'walletAmount',
      render: (v: number) => v !== undefined ? `₹${v.toLocaleString('en-IN')}` : '-' },
    { title: 'Status',   dataIndex: 'isActive', key: 'isActive', align: 'center' as const,
      render: (isActive: boolean) => <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'ACTIVE' : 'INACTIVE'}</Tag> },
  ];

  const openAddModal = () => {
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    form.resetFields();
  };

  const handleAddWallet = async (values: { employee: string; walletAmount: number; isActive?: boolean }) => {
    try {
      await addWallet({
        employee:     values.employee,
        walletAmount: values.walletAmount,
        isActive:     values.isActive ?? true,
      }).unwrap();
      // message.success('Wallet added successfully');
      closeAddModal();
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  return (
    <div className="wallet-page">
      <div className="header-section">
        <div className="left-col">
          <SearchBar
            value={searchText}
            onChange={(v: string) => { setSearchText(v); setPage(1); }}
            placeholder="Search wallets..."
          />
          <StatusTabs
            activeTab={activeTab}
            onTabChange={(tab: string) => { setActiveTab(tab); setPage(1); }}
            tabs={tabsData}
          />
        </div>
        <div className="right-col">
          {hasPermission('WALLET_CREATE') && (
            <AddButton onClick={openAddModal}>Add Wallet</AddButton>
          )}
        </div>
      </div>

      <div className="wallets-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={walletData}
          loading={walletsLoading}
          pagination={false}
          rowKey={(record: WalletRecord) => record.key}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={allWallets.length}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />

      <Modal
        open={addModalOpen}
        onCancel={closeAddModal}
        title="Add Wallet"
        okText="Add"
        onOk={() => form.submit()}
        confirmLoading={addingWallet}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleAddWallet}>
          <Form.Item name="employee" label="Select Employee" rules={[{ required: true, message: 'Please select an employee' }]}>
            <Select
              placeholder="Choose employee"
              loading={employeesLoading}
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={employeeOptions}
            />
          </Form.Item>

          <Form.Item name="walletAmount" label="Wallet Amount" rules={[{ required: true, message: 'Please enter wallet amount' }]}>
            <InputNumber style={{ width: '100%' }} min={0} placeholder="Enter amount" />
          </Form.Item>

          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Wallet;
